import { mediaQueries, Text, style } from '@do/walrus';
import React from 'react';
import styled from 'styled-components';
import { FORCE_EXTERNAL, HEADER_HEIGHT } from '../constants';
import copy from '../copy';
import Link from './Link';
import { registrationTrackingIds } from '../tracking-ids';
import { headerStyle, useRegistrationTemplate } from './RegistrationTemplateProvider';
import { isAMD } from '../utils/checkLocationHost';

const Container = styled.header<{ headerStyle: headerStyle }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${HEADER_HEIGHT}px;
  padding-left: ${style.vars.space['5']};
  padding-right: ${style.vars.space['5']};
  background: ${(props) => (props.headerStyle.background ? props.headerStyle.background : style.colors.white)};
  color: ${(props) => (props.headerStyle.color ? props.headerStyle.color : 'inherit')};
  z-index: ${style.vars.zIndex.raised};
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.25);
`;

const Preamble = styled.span<{ headerStyle: headerStyle }>`
  color: ${(props) => (props.headerStyle.color ? props.headerStyle.color : 'inherit')};
  display: none;
  margin-right: ${style.vars.space['3']};
  font-size: ${style.vars.fontSize.small};

  ${mediaQueries.mediumMin} {
    display: inline-block;
  }
`;

const Divider = styled.div`
  display: none;
  width: 1px;
  height: 24px;
  background-color: #636466;
  margin: 0 ${style.vars.space['3']};

  ${mediaQueries.mediumMin} {
    display: block;
  }
`;

const LogoText = styled.span`
  display: none;
  font-size: ${style.vars.fontSize.larger};
  font-family: 'Open Sans', sans-serif;
  font-weight: ${style.vars.fontWeight.bold};
  color: ${style.colors.white};

  ${mediaQueries.mediumMin} {
    display: block;
  }
`;
const DesktopLogo = styled.img<{ isAmd?: boolean }>`
  display: none;
  width: ${({ isAmd }) => (isAmd ? '102px' : '100%')};
  height: ${({ isAmd }) => (isAmd ? '24px' : '30px')};

  ${mediaQueries.mediumMin} {
    display: block;
  }
`;

const MobileLogo = styled.img`
  display: block;
  max-width: 100px;
  height: 30px;
  width: 100%;

  ${mediaQueries.mediumMin} {
    display: none;
  }
`;

const StyledActionLink = styled(Link)<{ color?: string }>`
  color: ${(props) => props.color ?? 'inherit'};
`;

interface Props {
  url?: string;
  isLogin?: boolean;
  forceExternal?: boolean;
  queryString?: string;
  showSignup?: boolean;
}

const Header: React.FC<Props> = ({
  isLogin,
  url = isLogin ? '/registrations/new' : '/login',
  forceExternal = false,
  queryString = '',
  showSignup = true,
}) => {
  const text = isLogin ? copy.header.login : copy.header.register;
  const { template } = useRegistrationTemplate();
  const headerStyle = template?.header;
  const _isAmd = isAMD();
  const signLinkTextColor = isLogin ? template?.topNav?.signUpTextColor : template?.topNav?.signInTextColor;

  return (
    <Container headerStyle={headerStyle}>
      <div className="do-u-flex do-u-flexMiddle">
        <Link
          target="_self"
          to={template.topNav.logo.link}
          data-tracking-id={registrationTrackingIds.header.logoButton}
        >
          <DesktopLogo src={template.topNav.logo.desktopLogo} isAmd={_isAmd} />
          <MobileLogo src={template.topNav.logo.mobileLogo} />
        </Link>
        {_isAmd && (
          <>
            <Divider />
            <LogoText>Developer Cloud</LogoText>
          </>
        )}
      </div>

      {showSignup && (
        <Text>
          <Preamble headerStyle={headerStyle}>{text.preamble}</Preamble>
          <StyledActionLink
            target="_self"
            color={signLinkTextColor}
            forceExternal={forceExternal || FORCE_EXTERNAL}
            to={url + queryString}
            data-tracking-id={registrationTrackingIds.header.signupButton}
          >
            {text.link_text}
          </StyledActionLink>
        </Text>
      )}
    </Container>
  );
};

export const SimpleHeader: React.FC = () => {
  const { template } = useRegistrationTemplate();
  const headerStyle = template?.header;
  const _isAmd = isAMD();

  return (
    <Container headerStyle={headerStyle}>
      <div className="do-u-flex do-u-flexMiddle">
        <Link
          target="_self"
          to={template.topNav.logo.link}
          data-tracking-id={registrationTrackingIds.header.logoButton}
        >
          <DesktopLogo src={template.topNav.logo.desktopLogo} isAmd={_isAmd} />
          <MobileLogo src={template.topNav.logo.mobileLogo} />
        </Link>
        {_isAmd && (
          <>
            <Divider />
            <LogoText>Developer Cloud</LogoText>
          </>
        )}
      </div>
    </Container>
  );
};

export default Header;
